import type { Editor as EditorType } from 'hugerte';

const STOCK_SHORTCUT_PATTERN = /alt\s*[+-]\s*0/i;

type FixEditAreaAriaOptions = {
	/** Corgi-i18n translated text for the help shortcut, replacing hugerte's stale "ALT-0" token. */
	shortcutText: string;
	/** True on macOS and iOS, whose screen reader is VoiceOver. */
	isVoiceOverPlatform: boolean;
};

/**
 * Gives hugerte's content edit area an accessible name that screen readers are allowed to use.
 *
 * hugerte titles the content iframe with a bare "Rich Text Area" and puts its `iframe_aria_text`
 * option (by default "Rich Text Area. Press ALT-0 for help."), translated, as an `aria-label` on
 * the iframe's `<body>`. A `<body>` has no role that permits an author-supplied name, so that
 * label is a WCAG 4.1.2 violation (axe `aria-prohibited-attr`). The shortcut in the default text
 * is also stale: our hugerte patch rebinds it to Access+0 (see the hugerte patch under patches/).
 *
 * The full help text therefore goes on the iframe `title` — an iframe may be named, and NVDA
 * and JAWS announce the frame title when focus enters the editor. VoiceOver does not read
 * iframe titles (TinyMCE's finding in TINYMCE-13100), so on macOS/iOS the body is labelled
 * instead, with `role="textbox"`/`aria-multiline="true"` so the label sits on a role that
 * allows it. That clears the WCAG rule (axe `aria-prohibited-attr`) at the cost of axe's
 * best-practice `aria-allowed-role` warning on those platforms. Everywhere else the body's
 * `aria-label` is removed so the text isn't announced twice.
 *
 * The text is hugerte's own translation of `iframe_aria_text` in every locale
 * (config/hugerte/langs) with only the shortcut token swapped for `shortcutText`, so the iframe
 * tooltip and the label stay in the user's language. Translations without a recognisable
 * shortcut token are used verbatim; locales whose translation is empty fall back to the
 * untranslated option text.
 */
export const fixEditAreaAria = (
	editor: EditorType,
	{ shortcutText, isVoiceOverPlatform }: FixEditAreaAriaOptions,
): void => {
	// hugerte's option map is loosely typed, so narrow rather than trust the declared `string`.
	const configured: unknown = editor.options.get('iframe_aria_text');
	const sourceText = typeof configured === 'string' ? configured : '';
	// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- hugerte's own translate, not Corgi's i18n
	const translated = editor.translate(sourceText) || sourceText;
	// A replacer function keeps `$`-sequences in `shortcutText` literal instead of being replacement patterns.
	const label = translated.replace(STOCK_SHORTCUT_PATTERN, () => shortcutText);

	editor.iframeElement?.setAttribute('title', label);

	const body = editor.getBody();
	if (isVoiceOverPlatform) {
		body.setAttribute('role', 'textbox');
		body.setAttribute('aria-multiline', 'true');
		body.setAttribute('aria-label', label);
	} else {
		body.removeAttribute('aria-label');
	}
};
